/**
 * Facility records, inside Manifest Review (Isaiah Cotto, 2026-10-27; docs/facility-delivery/DESIGN.md 4, OPERATIONS.md).
 *
 * One row per building visit day. The residents in a day's batch come from the provider's rounds seen marks and the
 * manifest outcomes of that day (or a VA adds one, with a reason). Each resident carries what the building's records
 * setting asks for: the AUDIOGRAM already filed on them in eMerge (the VA includes it explicitly), and/or the eMerge
 * visit summary (a placeholder until Isaiah rules what it holds: preview only). Then, by the building's channel:
 *
 *   EMAIL       Send now: an expiring secure link to the verified recipient (FACILITY_RECORDS_SEND; off by default);
 *   PCC_UPLOAD  the assisted queue: one-click downloads named per the facility's PCC convention, then Uploaded (date);
 *   OTHER       Mark sent outside eMerge (how, and the day);
 *   and in every case Mark confirmed, reminders (computed when read, nothing sends by itself) and the exceptions.
 *
 * As a board (house rule): one table row per building visit day, the record DOCKED beside the table, a demonstrative
 * legend that is also the filter, synced scroll rails. The server decides every act again; a control is offered only
 * when the server's `can` and `switches` say it would be accepted.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from '../../components/new-components/ui/button';
import { Input } from '../../components/new-components/ui/input';
import { apiDownload } from '../../services/api';
import { MfLoadFailure, MfSkeleton } from './mfStates';
import { StateBlock as TideState } from '../../components/tide';
import MfBoard, { useClientSort, useOpenParam, type BoardColumn, type BoardDetailSection } from './board/MfBoard';
import MfLegend, { type MfLegendItem } from './board/MfLegend';
import { ToneChip, type Tone } from './board/chips';
import {
  CHANNEL_LABEL, VIEW_LABEL, exceptionWords, facilityRecordsApi, refusalWords,
  type Batch, type BatchResident, type Channel, type Desk, type DeskRow, type SettingsScreen, type View,
} from './facilityRecordsApi';
import './facilityRecords.css';

const ZONE = 'America/New_York';
const day = (ymd: string | null) => (ymd ? new Date(`${ymd.slice(0, 10)}T12:00:00Z`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' }) : '—');
const when = (iso: string | null) => (iso ? new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: ZONE }) : '—');
const todayNy = () => new Intl.DateTimeFormat('en-CA', { timeZone: ZONE, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
const who = (r: { first_name: string | null; last_name: string | null }) => [r.last_name, r.first_name].filter(Boolean).join(', ') || 'Name not on file';

const VIEW_TONE: Record<View, Tone> = {
  to_prepare: 'quiet', ready_to_send: 'ok', to_upload: 'ok', awaiting_confirmation: 'attn', exceptions: 'stop', done: 'ok',
};
const VIEW_MEANS: Record<View, string> = {
  to_prepare: 'Residents seen that day still need their files, a hold, or the batch is not marked complete.',
  ready_to_send: 'Complete; waiting for Send (email) or "Mark sent outside eMerge".',
  to_upload: 'A PCC building: files to upload into PCC and mark Uploaded.',
  awaiting_confirmation: 'Delivered; the facility has not confirmed yet. Reminders are due by the building\'s clocks.',
  exceptions: 'Something needs a person: a recipient, a resident who moved, a failed or unknown send, an expired link.',
  done: 'Confirmed by the facility, or uploaded into PCC.',
};
const VIEWS: View[] = ['to_prepare', 'ready_to_send', 'to_upload', 'awaiting_confirmation', 'exceptions', 'done'];

const ViewChip = ({ view }: { view: View }) => <ToneChip tone={VIEW_TONE[view]}>{VIEW_LABEL[view]}</ToneChip>;

/** A button that asks for a reason (or a note) before it acts. */
function ReasonAct({ label, ask, required = true, onAct, variant = 'outline', disabled = false }: {
  label: string; ask: string; required?: boolean; onAct: (text: string) => Promise<string | null>; variant?: 'outline' | 'default' | 'destructive' | 'secondary'; disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  if (!open) return <Button size="sm" variant={variant} type="button" onClick={() => setOpen(true)} disabled={disabled}>{label}</Button>;
  const short = required && text.trim().length < 3;
  return (
    <form className="fr-reason" aria-label={label} onSubmit={async (e) => {
      e.preventDefault();
      if (short) { setErr(`${ask} (at least 3 characters).`); return; }
      setBusy(true); setErr(null);
      const problem = await onAct(text.trim());
      setBusy(false);
      if (problem) setErr(problem); else { setOpen(false); setText(''); }
    }}>
      <Input value={text} onChange={(e) => setText(e.target.value)} maxLength={500} placeholder={ask} aria-label={ask} disabled={busy} />
      <Button size="sm" type="submit" disabled={busy}>{busy ? 'Saving…' : label}</Button>
      <Button size="sm" variant="ghost" type="button" onClick={() => { setOpen(false); setText(''); setErr(null); }} disabled={busy}>Cancel</Button>
      {err && <p className="mf-notice mf-notice--stop" role="alert">{err}</p>}
    </form>
  );
}

async function saveBlob(path: string, fallbackName: string): Promise<string | null> {
  try {
    const blob = await apiDownload(path);
    const href = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = href; a.download = fallbackName;
    document.body.appendChild(a); a.click(); a.remove();
    window.setTimeout(() => window.URL.revokeObjectURL(href), 0);
    return null;
  } catch { return 'The file could not be downloaded. Nothing was recorded as uploaded.'; }
}

/* ── One resident in the batch ─────────────────────────────────────────────────────────────────── */

function ResidentCard({ batch, r, act }: { batch: Batch; r: BatchResident; act: (p: Promise<{ success: boolean; message?: string; status?: number; code?: string; error?: string }>, done: string) => Promise<string | null> }) {
  const f = batch.facility_id; const d = batch.visit_date;
  const canPrep = batch.can.prepare && batch.switches.writes && !batch.completed;
  const pcc = batch.setting.current?.channel === 'PCC_UPLOAD';
  const [opening, setOpening] = useState<string | null>(null);
  const [linkErr, setLinkErr] = useState<string | null>(null);
  const open = async (documentId: string) => {
    setOpening(documentId); setLinkErr(null);
    const win = typeof window.open === 'function' ? window.open('', '_blank') : null;
    try {
      const res = await facilityRecordsApi.documentUrl(f, d, r.patient_id, documentId);
      if (res.success && res.data) { if (win) { win.opener = null; win.location.replace(res.data.url); } } else { if (win) win.close(); setLinkErr(refusalWords(res, 'The file could not be opened.')); }
    } catch { if (win) win.close(); setLinkErr('Could not reach eMerge.'); } finally { setOpening(null); }
  };
  return (
    <li className="fr-resident" data-testid={`fr-resident-${r.patient_id}`}>
      <div className="fr-resident-head">
        <strong>{who(r)}</strong>
        <span className="mf-sub">{r.room ? ` · Room ${r.room}` : ''} · {r.basis.map((b) => ({ ROUNDS: 'seen on rounds', MANIFEST: 'manifest outcome', MANUAL: 'added by a VA' }[b])).join(', ') || 'not in the batch'}</span>
        {!r.in_batch && <ToneChip tone="quiet">{r.removed ? `Removed: ${r.removed.reason}` : 'Not in the batch'}</ToneChip>}
        {r.held && <ToneChip tone="attn" title={r.held.reason}>Held by {r.held.by_first_name}</ToneChip>}
        {r.flag && <ToneChip tone={r.flag_accepted ? 'quiet' : 'stop'}>{r.flag === 'MOVED' ? 'Moved since the visit' : 'Recorded deceased'}{r.flag_accepted ? ' · send accepted' : ''}</ToneChip>}
        {r.in_batch && !r.held && (r.complete && !r.needs_decision ? <ToneChip tone="ok">Ready</ToneChip> : <ToneChip tone="attn">Missing {r.missing.map((m) => ({ AUDIOGRAM: 'audiogram', VISIT_SUMMARY: 'visit summary', VISIT_NOTE: "the provider's visit note" }[m])).join(' and ') || 'a decision'}</ToneChip>)}
        {r.in_batch && r.pending.includes('AUDIOGRAM') && <ToneChip tone="attn" title="Not filed when the provider closed out: it goes in a follow-up send once it is filed (Send a new link).">Audiogram pending</ToneChip>}
      </div>
      {r.in_batch && (
        <>
          <ul className="fr-docs" aria-label={`Filed audiograms for ${who(r)}`}>
            {r.documents.length === 0 && <li className="mf-sub">No audiogram is filed on this resident in eMerge. A VA or PA files it on the resident first (UMED or PA Documents).</li>}
            {r.documents.map((doc) => {
              const deliverable = r.deliverables.find((x) => x.document_id === doc.document_id);
              return (
                <li key={doc.document_id}>
                  <span>Audiogram{doc.test_date ? ` · tested ${day(doc.test_date)}` : ' · test date not recorded'} · filed {when(doc.filed_at)}{doc.on_merged_record ? ' · on the merged record' : ''}</span>
                  {doc.problem && <ToneChip tone="stop">{doc.problem === 'NOT_THIS_RESIDENTS' ? 'Not this resident\'s' : doc.problem === 'NOT_DELIVERABLE' ? 'Not a PDF or image under 20 MB' : 'No longer available'}</ToneChip>}
                  {doc.included && !doc.problem && <ToneChip tone="ok">In the batch</ToneChip>}
                  <span className="fr-acts">
                    {doc.document_class && <Button size="sm" variant="ghost" type="button" onClick={() => open(doc.document_id)} disabled={opening === doc.document_id}>{opening === doc.document_id ? 'Opening…' : 'Open'}</Button>}
                    {canPrep && !doc.included && !doc.problem && (
                      <Button size="sm" variant="outline" type="button" onClick={() => act(facilityRecordsApi.item(f, d, r.patient_id, { source_kind: 'DOCUMENT', document_id: doc.document_id, include: true }), `${who(r)}: audiogram included.`)}>Include for this visit</Button>
                    )}
                    {canPrep && doc.included && (
                      <ReasonAct label="Leave out" ask="Why it is left out" onAct={(reason) => act(facilityRecordsApi.item(f, d, r.patient_id, { source_kind: 'DOCUMENT', document_id: doc.document_id, include: false, reason }), `${who(r)}: audiogram left out.`)} />
                    )}
                    {pcc && deliverable && batch.completed && (
                      <>
                        <Button size="sm" variant="outline" type="button" onClick={async () => { const p = await saveBlob(facilityRecordsApi.pccFilePath(f, d, r.patient_id, 'DOCUMENT', doc.document_id), 'audiogram.pdf'); if (p) setLinkErr(p); }}>Download for PCC</Button>
                        {deliverable.uploaded
                          ? <ToneChip tone="ok">Uploaded {day(deliverable.uploaded.pcc_date)} by {deliverable.uploaded.by_first_name}</ToneChip>
                          : batch.can.prepare && batch.switches.writes && <Button size="sm" type="button" onClick={() => act(facilityRecordsApi.pccUploaded(f, d, todayNy(), [{ patient_id: r.patient_id, source_kind: 'DOCUMENT', document_id: doc.document_id }]), `${who(r)}: marked uploaded to PCC today.`)}>Uploaded to PCC</Button>}
                      </>
                    )}
                  </span>
                </li>
              );
            })}
            {r.summary.note && r.summary.note.kind === 'NO_NOTE_NEEDED' && (
              <li><span>No visit note needed (the provider): {r.summary.note.reason}</span></li>
            )}
            {r.summary.wanted && (
              <li>
                <span>Visit note{r.summary.note ? (r.summary.note.kind === 'NOTE' ? ` · version ${r.summary.note.version} by ${r.summary.note.written_by_first_name || r.summary.note.by_first_name}${r.summary.note.edited_by_first_name ? `, edited by ${r.summary.note.edited_by_first_name}` : ''}, ${when(r.summary.note.at)}` : '') : ' · not written yet'} · {r.summary.included ? 'included' : r.summary.note ? 'left out' : 'waiting for the provider'}</span>
                {!r.summary.approved && <ToneChip tone="quiet" title="The visit summary template is not approved for sending: preview only.">Preview only</ToneChip>}
                <span className="fr-acts">
                  <Button size="sm" variant="ghost" type="button" onClick={async () => { const p = await saveBlob(facilityRecordsApi.summaryPreviewPath(f, d, r.patient_id), 'visit-summary-preview.pdf'); if (p) setLinkErr(p); }}>Preview</Button>
                  {canPrep && (r.summary.included
                    ? <ReasonAct label="Leave out" ask="Why the summary is left out" onAct={(reason) => act(facilityRecordsApi.item(f, d, r.patient_id, { source_kind: 'VISIT_SUMMARY', include: false, reason }), `${who(r)}: summary left out.`)} />
                    : <Button size="sm" variant="outline" type="button" onClick={() => act(facilityRecordsApi.item(f, d, r.patient_id, { source_kind: 'VISIT_SUMMARY', include: true }), `${who(r)}: summary included.`)}>Include</Button>)}
                </span>
              </li>
            )}
          </ul>
          {linkErr && <p className="mf-notice mf-notice--stop" role="alert">{linkErr}</p>}
          {canPrep && (
            <div className="fr-acts">
              {r.needs_decision && r.flag && (
                <ReasonAct label="Send to this building anyway" ask="Why these records still go to this building" onAct={(reason) => act(facilityRecordsApi.acceptChange(f, d, r.patient_id, r.flag as string, reason), `${who(r)}: will be sent to this building.`)} />
              )}
              {r.held
                ? <ReasonAct label="Release hold" ask="Why the hold is released" onAct={(reason) => act(facilityRecordsApi.hold(f, d, r.patient_id, false, reason), `${who(r)}: hold released.`)} />
                : <ReasonAct label="Hold" ask="Why this resident is held" onAct={(reason) => act(facilityRecordsApi.hold(f, d, r.patient_id, true, reason), `${who(r)}: held.`)} />}
              <ReasonAct label="Remove from batch" variant="secondary" ask="Why this resident is not in this visit's records" onAct={(reason) => act(facilityRecordsApi.removeResident(f, d, r.patient_id, reason), `${who(r)}: removed from the batch.`)} />
            </div>
          )}
        </>
      )}
    </li>
  );
}

/* ── Add a resident by hand ─────────────────────────────────────────────────────────────────────── */

function AddResident({ batch, act }: { batch: Batch; act: (p: Promise<any>, done: string) => Promise<string | null> }) {
  const [q, setQ] = useState('');
  const [hits, setHits] = useState<Array<{ patient_id: string; first_name: string | null; last_name: string | null; room: string | null }>>([]);
  const [pick, setPick] = useState<string | null>(null);
  useEffect(() => {
    if (q.trim().length < 2) { setHits([]); return undefined; }
    let live = true;
    const t = setTimeout(async () => {
      const r = await facilityRecordsApi.candidates(batch.facility_id, batch.visit_date, q.trim());
      if (live && r.success && r.data) setHits(r.data.items);
    }, 250);
    return () => { live = false; clearTimeout(t); };
  }, [q, batch.facility_id, batch.visit_date]);
  const chosen = hits.find((h) => h.patient_id === pick) || null;
  return (
    <div className="fr-add">
      <Input value={q} onChange={(e) => { setQ(e.target.value); setPick(null); }} placeholder="Add a resident seen but not marked: type a name" aria-label="Find a resident confirmed at this building" />
      {hits.length > 0 && !chosen && (
        <ul className="fr-hits" aria-label="Residents confirmed at this building">
          {hits.map((h) => <li key={h.patient_id}><Button size="sm" variant="ghost" type="button" onClick={() => setPick(h.patient_id)}>{who(h)}{h.room ? ` · Room ${h.room}` : ''}</Button></li>)}
        </ul>
      )}
      {chosen && (
        <ReasonAct label={`Add ${who(chosen)}`} ask="Why this resident belongs in the batch" onAct={async (reason) => {
          const p = await act(facilityRecordsApi.addResident(batch.facility_id, batch.visit_date, chosen.patient_id, reason), `${who(chosen)}: added to the batch.`);
          if (!p) { setQ(''); setPick(null); setHits([]); }
          return p;
        }} />
      )}
    </div>
  );
}

/* ── The delivery block ─────────────────────────────────────────────────────────────────────────── */

/** What the first-send delay's stop codes mean, in words. */
const STOP_WORDS: Record<string, string> = {
  EXPIRED: 'it was not sent within two hours of its time (the server was down or sending was switched off), so it was not sent late',
  SETTING_CHANGED: 'the records setting changed while it waited',
  RECIPIENT_NOT_READY: 'a recipient changed or was ended in Contacts, or the confirmation was withdrawn, while it waited',
  BUILDING_CLOSED: 'the building was closed while it waited',
  BATCH_NOT_COMPLETE: 'the batch was reopened while it waited',
  BATCH_NOT_READY: 'a resident needs something before the batch can go',
  QUEUER_NOT_ALLOWED: 'the person who sent it is no longer active or no longer allowed to send for this building',
  FACILITY_RECORDS_WRITES_OFF: 'facility records changes were switched off on the server (FACILITY_RECORDS_WRITES) while it waited',
};

/** The first-send delay on the record: waiting (with Cancel and, once due, Send now), cancelled, or stopped and why. */
function FirstSend({ batch, act }: { batch: Batch; act: (p: Promise<any>, done: string | ((r: any) => string)) => Promise<string | null> }) {
  const f = batch.facility_id; const d = batch.visit_date;
  const q = batch.queued_send;
  if (q) {
    return (
      <div className="fr-first-send" data-testid="fr-queued">
        <p><ToneChip tone="attn">Sending at {easternClock(q.send_at)} Eastern</ToneChip> · the first send to these recipients waits 15 minutes{q.by_first_name ? ` (sent by ${q.by_first_name})` : ''}.</p>
        {batch.can.send && batch.switches.writes && (
          <div className="fr-acts">
            <ReasonAct label="Cancel the send" ask="Why the first send is cancelled" onAct={(reason) => act(facilityRecordsApi.cancelQueued(f, d, reason), 'The first send was cancelled. Nothing was sent.')} />
            {q.due && <Button size="sm" type="button" onClick={() => act(facilityRecordsApi.sendQueuedNow(f, d), (r) => (r.data && r.data.sent ? 'Sent.' : 'Not sent: see why below.'))}>Send now</Button>}
          </div>
        )}
      </div>
    );
  }
  if (batch.first_send_stopped) {
    const code = String(batch.first_send_stopped.reason || '');
    return <p className="mf-notice mf-notice--stop" role="status" data-testid="fr-first-send-stopped">The first send was stopped at {easternClock(batch.first_send_stopped.at)} Eastern and nothing was sent: {STOP_WORDS[code] || code}. Fix it, then send again.</p>;
  }
  if (batch.first_send_cancelled) {
    return <p className="mf-sub" data-testid="fr-first-send-cancelled">The first send was cancelled{batch.first_send_cancelled.by_first_name ? ` by ${batch.first_send_cancelled.by_first_name}` : ''} at {easternClock(batch.first_send_cancelled.at)} Eastern: {batch.first_send_cancelled.reason}</p>;
  }
  return null;
}

const easternClock = (t: string) => new Date(t).toLocaleTimeString('en-US', { timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit' });

function Delivery({ batch, act }: { batch: Batch; act: (p: Promise<any>, done: string | ((r: any) => string)) => Promise<string | null> }) {
  const f = batch.facility_id; const d = batch.visit_date;
  const s = batch.setting.current;
  const channel: Channel | null = s ? s.channel : null;
  const writes = batch.switches.writes;
  const prep = batch.can.prepare && writes;
  const [method, setMethod] = useState('PHONE');
  const [onDay, setOnDay] = useState(todayNy());
  const [outsideMethod, setOutsideMethod] = useState('EMAIL_OUTSIDE');
  const sendBlocked = !batch.switches.send ? 'Sending from eMerge is switched off on this server (FACILITY_RECORDS_SEND).'
    : batch.config && !batch.config.reply_to_configured ? 'Reply-To not configured: this server has no reply mailbox for facility records (FACILITY_RECORDS_REPLY_TO).'
    : !batch.setting.sendable ? 'The recipient is not ready: see the exceptions.'
      : !batch.completed ? 'Mark the batch complete first.' : null;
  return (
    <div className="fr-delivery">
      <p>
        <strong>{channel ? CHANNEL_LABEL[channel] : 'No records setting yet'}</strong>
        {batch.setting.recipients[0] && <> · to {batch.setting.recipients[0].role ? `${batch.setting.recipients[0].role.replace(/_/g, ' ').toLowerCase()} ` : ''}{batch.setting.recipients[0].name}</>}
        {channel === 'EMAIL' && (batch.setting.verified ? <> · <ToneChip tone="ok">{confirmedWords(batch.setting.verification)}</ToneChip></> : <> · <ToneChip tone="stop">Recipients not confirmed</ToneChip></>)}
      </p>
      {!writes && <p className="mf-notice" role="status">Facility records changes are switched off on this server (FACILITY_RECORDS_WRITES). Everything here is read-only.</p>}
      {batch.exceptions.length > 0 && (
        <ul className="fr-exceptions" aria-label="Exceptions">{batch.exceptions.map((x) => <li key={x}><ToneChip tone="stop">{exceptionWords(x)}</ToneChip></li>)}</ul>
      )}
      <div className="fr-acts">
        {prep && !batch.completed && <Button size="sm" type="button" disabled={!batch.all_complete} onClick={() => act(facilityRecordsApi.complete(f, d), 'Batch marked complete.')}>Batch complete</Button>}
        {prep && batch.completed && <ReasonAct label="Reopen batch" ask="Why the batch is reopened" onAct={(reason) => act(facilityRecordsApi.reopen(f, d, reason), 'Batch reopened.')} />}
        {channel === 'EMAIL' && batch.can.send && (
          <Button size="sm" type="button" disabled={Boolean(sendBlocked)} title={sendBlocked || undefined}
            onClick={() => act(facilityRecordsApi.send(f, d, s ? s.version : 0, batch.attempts.length), (r) => (r.data && r.data.outcome === 'QUEUED'
              ? `Queued: the first send to these recipients goes at ${easternClock(r.data.send_at)} Eastern. You can cancel it until then.`
              : 'Sent: a secure link went to the recipient.'))}>
            {batch.attempts.length ? 'Send a new link' : 'Send now'}
          </Button>
        )}
      </div>
      {channel === 'EMAIL' && batch.can.send && sendBlocked && <p className="mf-sub">{sendBlocked}</p>}
      {channel === 'EMAIL' && <FirstSend batch={batch} act={act} />}
      {prep && batch.completed && channel && channel !== 'HOLD' && (
        <div className="fr-acts">
          <select aria-label="How it was sent outside eMerge" value={outsideMethod} onChange={(e) => setOutsideMethod(e.target.value)}>
            <option value="EMAIL_OUTSIDE">Email (Outlook)</option><option value="FAX_OUTSIDE">Fax machine</option><option value="HAND_DELIVERED">By hand</option><option value="OTHER">Other</option>
          </select>
          <ReasonAct label="Mark sent outside eMerge" ask="Note (optional)" required={false} onAct={(note) => act(facilityRecordsApi.outsideSent(f, d, outsideMethod, todayNy(), note), 'Recorded as sent outside eMerge today.')} />
        </div>
      )}
      {channel === 'PCC_UPLOAD' && batch.completed && prep && batch.counts.uploaded < batch.counts.files && (
        <div className="fr-acts">
          <Button size="sm" type="button" onClick={() => act(facilityRecordsApi.pccUploaded(f, d, todayNy()), 'Every file marked uploaded to PCC today.')}>All uploaded to PCC today</Button>
        </div>
      )}
      {(batch.delivered_at || batch.counts.uploaded > 0) && (
        <div className="fr-confirm">
          {batch.confirmed
            ? <p><ToneChip tone="ok">Confirmed {day(batch.confirmed.confirmed_on)}{batch.confirmed.by === 'RECIPIENT_LINK' ? ' by the facility (secure link)' : ` (${batch.confirmed.method.toLowerCase().replace(/_/g, ' ')}) by ${batch.confirmed.by_first_name}`}</ToneChip>
              {prep && <ReasonAct label="Undo confirmation" variant="secondary" ask="Why the confirmation is undone" onAct={(reason) => act(facilityRecordsApi.undoConfirmation(f, d, reason), 'Confirmation undone.')} />}</p>
            : prep && (
              <div className="fr-acts">
                <select aria-label="How the facility confirmed" value={method} onChange={(e) => setMethod(e.target.value)}>
                  <option value="PHONE">Phone</option><option value="EMAIL_REPLY">Email reply</option><option value="IN_PERSON">In person</option><option value="PCC_UPLOAD">PCC upload</option>
                </select>
                <Input type="date" value={onDay} max={todayNy()} onChange={(e) => setOnDay(e.target.value)} aria-label="The day the facility confirmed" className="fr-date" />
                <ReasonAct label="Mark confirmed" ask="Note (optional, never shown to the facility)" required={false} onAct={(note) => act(facilityRecordsApi.confirmed(f, d, method, onDay, note), 'Confirmation recorded.')} />
              </div>
            )}
          {batch.reminder && (
            <p className="mf-sub">
              Waiting {batch.reminder.business_days_waiting} business {batch.reminder.business_days_waiting === 1 ? 'day' : 'days'} since {day(batch.reminder.delivered_on)}.
              {batch.reminder.escalated ? ' Escalated: call the building to confirm.' : batch.reminder.due ? ' A reminder is due.' : ''}
              {batch.reminder.last_reminded_at ? ` Last reminded ${when(batch.reminder.last_reminded_at)}.` : ''}
            </p>
          )}
          {prep && batch.queue === 'sent' && <ReasonAct label="Record a reminder (phone)" ask="Who you reached (optional)" required={false} onAct={(note) => act(facilityRecordsApi.reminded(f, d, 'PHONE', note), 'Reminder recorded.')} />}
        </div>
      )}
      {(batch.links.length > 0 || batch.attempts.length > 0) && (
        <ul className="fr-links" aria-label="Secure links">
          {batch.links.map((l) => {
            const a = batch.attempts.find((x) => x.attempt === l.attempt);
            return (
              <li key={l.link_id}>
                <span>Link {l.attempt} · {a?.outcome ? a.outcome.toLowerCase() : 'claimed'} {when(a?.outcome_at || l.created_at)} · {l.state.toLowerCase().replace('_', ' ')} · expires {when(l.expires_at)} · opened {l.opened} · downloads {l.files.reduce((n, x) => n + x.downloads, 0)}{l.code_failures ? ` · ${l.code_failures} wrong codes` : ''}{l.locked_addresses ? ` · ${l.locked_addresses} ${l.locked_addresses === 1 ? 'address' : 'addresses'} locked` : ''}</span>
                {batch.can.send && writes && !l.revoked && l.state !== 'NOT_SENT' && <ReasonAct label="Revoke link" variant="destructive" ask="Why the link is revoked" onAct={(reason) => act(facilityRecordsApi.revoke(f, d, l.link_id, reason), `Link ${l.attempt} revoked.`)} />}
                {batch.can.send && writes && a && a.outcome === 'UNKNOWN' && !a.released && (
                  <>
                    <ReasonAct label="It went (in Sent Items)" ask="What you checked" onAct={(note) => act(facilityRecordsApi.release(f, d, a.attempt, 'WENT', note), 'Released as sent.')} />
                    <ReasonAct label="It did not go" ask="What you checked" onAct={(note) => act(facilityRecordsApi.release(f, d, a.attempt, 'DID_NOT_GO', note), 'Released as not sent.')} />
                  </>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {batch.outside.length > 0 && <p className="mf-sub">Sent outside eMerge: {batch.outside.map((o) => `${o.method.toLowerCase().replace(/_/g, ' ')} on ${day(o.sent_on)} by ${o.by_first_name}`).join('; ')}.</p>}
      {batch.exceptions.includes('RECALL_NEEDED') && batch.can.send && writes && (
        <div className="fr-acts">
          {batch.residents.filter((r) => r.documents.some((x) => x.problem === 'NOT_THIS_RESIDENTS')).map((r) => (
            <ReasonAct key={r.patient_id} label={`Recall ${who(r)}'s file`} variant="destructive" ask="What is recalled and how the facility was told" onAct={(reason) => act(facilityRecordsApi.recall(f, d, r.patient_id, r.documents.find((x) => x.problem === 'NOT_THIS_RESIDENTS')?.document_id || null, reason), 'Recalled; the link is revoked. Tell the facility.')} />
          ))}
        </div>
      )}
    </div>
  );
}

/* ── The building's records setting ─────────────────────────────────────────────────────────────── */

const CHANGE_LABEL: Record<string, string> = { ADDED: 'added', EDITED: 'edited', REPLACED: 'replaced', ADOPTED: 'adopted' };

/** How the recipients were confirmed, in words (ruling 2026-10-28: the contacts on file; or a call-back). */
function confirmedWords(v: { method?: string | null; at: string; by_first_name: string } | null): string {
  if (!v) return 'Recipients confirmed';
  const how = v.method === 'CALLBACK_MAIN' ? 'Verified by call-back' : 'Confirmed: the contacts on file';
  return `${how} ${when(v.at)} by ${v.by_first_name}`;
}

/** One recipient as the VA sees who will get the records: To or Copy, name, role, title, address. */
const recipientLine = (r: { primary: boolean; name: string | null; role: string | null; title?: string | null; email: string | null }) =>
  `${r.primary ? 'To: ' : 'Copy: '}${r.name || '—'} (${(r.role || '').replace(/_/g, ' ').toLowerCase()}${r.title ? `, ${r.title}` : ''}) ${r.email || 'no email'}`;

function SettingPanel({ facilityId, onChanged }: { facilityId: string; onChanged: () => void }) {
  const [s, setS] = useState<SettingsScreen | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<Record<string, any>>({});
  // Recipients the open form dropped because they are no longer current contacts (the MISSING_RECIPIENT way out).
  const [dropped, setDropped] = useState<string[]>([]);
  const load = useCallback(async () => {
    const r = await facilityRecordsApi.settings(facilityId);
    if (r.success && r.data) { setS(r.data); setErr(null); } else setErr(refusalWords(r, 'The setting could not be loaded.'));
  }, [facilityId]);
  useEffect(() => { load(); }, [load]);
  if (err) return <p className="mf-notice mf-notice--stop" role="alert">{err}</p>;
  if (!s) return <MfSkeleton bare />;
  const c = s.current;
  // The recipients the open form names, in order, and those the person ticking must look at again (verifier re-check #5).
  const chosenContacts = [form.recipient_contact_record_id, ...(form.cc_contact_record_ids || [])].filter(Boolean)
    .map((id: string, i: number) => ({ ct: s.contacts.find((x) => x.id === id), primary: i === 0 }))
    .filter((x): x is { ct: SettingsScreen['contacts'][number]; primary: boolean } => Boolean(x.ct));
  const needsAck = chosenContacts.filter(({ ct }) => (ct.recent_change && !ct.recent_change.by_you) || ct.new_domain);
  // A contact whose note in Contacts says not to send there (Gwynn's exclusions) needs its own explicit tick.
  const markedDoNotSend = chosenContacts.filter(({ ct }) => ct.do_not_send);
  const canConfigure = s.can.configure && s.switches.writes;
  const maxCopies = s.limits?.max_copies ?? 2;
  const startEdit = () => {
    // Only CURRENT contacts can be chosen: a recipient or copy ended or replaced since (MISSING_RECIPIENT) is dropped
    // here and named, so the new version saves in one go instead of being refused for a contact nobody can see.
    const current = new Set(s.contacts.map((ct) => String(ct.id).toLowerCase()));
    const isCurrent = (id: string) => current.has(String(id).toLowerCase());
    const nameOf = (id: string) => {
      const r = s.recipients.find((x) => String(x.id).toLowerCase() === String(id).toLowerCase());
      return r ? `${r.name || r.email || 'a contact'}${r.primary ? ' (the recipient)' : ''}` : 'a contact';
    };
    const recipient = c?.recipient_contact_record_id && isCurrent(c.recipient_contact_record_id) ? c.recipient_contact_record_id : '';
    const copies = (c?.cc_contact_record_ids || []).filter(isCurrent);
    setDropped([...(c?.recipient_contact_record_id && !recipient ? [c.recipient_contact_record_id] : []), ...(c?.cc_contact_record_ids || []).filter((id) => !isCurrent(id))].map(nameOf));
    // Every field of the current version is carried; only "who asked" is asked again for each new version.
    setForm({
      channel: c?.channel || 'HOLD', recipient_contact_record_id: recipient, artifacts: c?.artifacts || 'AUDIOGRAM',
      cc_contact_record_ids: copies,
      format: c?.format || 'PER_RESIDENT_PDFS', cadence: c?.cadence || 'PER_VISIT_DAY', weekly_day: c?.weekly_day ?? null,
      confirmation_required: c?.confirmation_required ?? true, upload_is_confirmation: c?.upload_is_confirmation ?? true,
      remind_after_business_days: c?.remind_after_business_days ?? 2, escalate_after_business_days: c?.escalate_after_business_days ?? 5,
      pcc_document_category: c?.pcc_document_category || '', pcc_filename_pattern: c?.pcc_filename_pattern || '',
      allow_webmail: c ? Boolean(c.allow_webmail) : false, webmail_reason: c?.webmail_reason || '', note: c?.note || '',
      request_basis: '', contacts_on_file: false, contacts_recent_change_ack: false, contacts_exclusion_ack: false,
    });
    setEditing(true);
  };
  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    const body: Record<string, unknown> = { ...form, expected_version: c ? c.version : 0 };
    if (!body.recipient_contact_record_id) delete body.recipient_contact_record_id;
    // Copies are for email buildings; the recipient is never also a copy.
    body.cc_contact_record_ids = form.channel === 'EMAIL' ? (form.cc_contact_record_ids || []).filter((id: string) => id !== form.recipient_contact_record_id) : [];
    if (!body.pcc_filename_pattern) delete body.pcc_filename_pattern;
    if (!body.pcc_document_category) delete body.pcc_document_category;
    if (!body.note) delete body.note;
    // The webmail permission belongs to email buildings only: another channel never saves it (verifier LOW).
    if (form.channel !== 'EMAIL') body.allow_webmail = false;
    body.contacts_on_file = form.channel === 'EMAIL' && Boolean(form.contacts_on_file);
    body.contacts_recent_change_ack = Boolean(body.contacts_on_file && form.contacts_recent_change_ack);
    body.contacts_exclusion_ack = Boolean(form.channel === 'EMAIL' && form.contacts_exclusion_ack);
    if (!body.allow_webmail || !body.webmail_reason) delete body.webmail_reason;
    if (body.weekly_day === null || body.weekly_day === '') delete body.weekly_day;
    body.remind_after_business_days = Number(form.remind_after_business_days);
    body.escalate_after_business_days = Number(form.escalate_after_business_days);
    const r = await facilityRecordsApi.saveSetting(facilityId, body);
    if (r.success) {
      setEditing(false); setDropped([]);
      setNotice(r.data?.contacts_on_file
        ? `Saved as version ${r.data?.version}. These contacts on file receive the records.`
        : `Saved as version ${r.data?.version}. ${form.channel === 'EMAIL' ? 'The recipients are not confirmed yet: nothing is sent until they are.' : ''}`);
      await load(); onChanged();
    } else setNotice(refusalWords(r, 'Not saved.'));
  };
  return (
    <div className="fr-setting">
      {c ? (
        <dl className="fr-dl">
          <dt>Channel</dt><dd>{CHANNEL_LABEL[c.channel]}</dd>
          <dt>{s.recipients.length > 1 ? `Recipients (${s.recipients.length})` : 'Recipient'}</dt>
          <dd>
            {c.channel === 'EMAIL' && <p className="mf-sub">Recipients: the contacts on file (change them under Contacts).</p>}
            {s.recipients.length ? (
              <ul className="fr-recipients" aria-label="Recipients">
                {s.recipients.map((r) => <li key={r.id}>{recipientLine(r)}{r.problems.length ? ` · ${r.problems.join(', ').toLowerCase()}` : ''}</li>)}
              </ul>
            ) : 'none'}
          </dd>
          <dt>What is sent</dt><dd>{c.artifacts === 'BOTH' ? 'Audiogram and visit summary' : c.artifacts === 'AUDIOGRAM' ? 'Audiogram' : 'Visit summary'}</dd>
          <dt>Requested by</dt><dd>{c.request_basis}</dd>
          <dt>Confirmed</dt><dd>{s.verified ? confirmedWords(s.verification) : 'Not confirmed: change the setting and tick "Send to these contacts on file"'}</dd>
          <dt>Version</dt><dd>{c.version}, set {when(c.recorded_at)} by {c.set_by.name}</dd>
        </dl>
      ) : <p className="mf-sub">This building has no records setting. Until one is set, nothing is sent (HOLD).</p>}
      {c && c.channel === 'EMAIL' && s.exceptions.includes('MISSING_RECIPIENT') && (
        <p className="mf-notice mf-notice--stop" role="status" data-testid="fr-contact-changed">A contact changed: confirm the recipients again. Nothing is sent to this building until the setting is saved with its current contacts on file.</p>
      )}
      {notice && <p className="mf-notice" role="status">{notice}</p>}
      {canConfigure && !editing && (
        <div className="fr-acts">
          <Button size="sm" variant="outline" type="button" onClick={startEdit}>Change setting</Button>
          {c && c.channel === 'EMAIL' && !s.verified && !s.you_set_current && s.main_phone && (
            <ReasonAct label="Record call-back verification" ask={`Who you reached at the main number${s.main_phone ? ` (${s.main_phone.phone})` : ''} and that they confirmed ${s.recipients.length === 1 ? 'the recipient' : `all ${s.recipients.length} recipients (${s.recipients.map((r) => r.name || r.email || '—').join(', ')})`}`}
              onAct={async (note) => { const r = await facilityRecordsApi.verify(facilityId, c.version, note); if (!r.success) return refusalWords(r, 'Not recorded.'); setNotice('Verified.'); await load(); onChanged(); return null; }} />
          )}
          {c && c.channel === 'EMAIL' && !s.verified && <p className="mf-sub">Or verify by call-back instead: a second person calls the building's main line{s.you_set_current ? ' (not you: you set this version)' : ''}.</p>}
        </div>
      )}
      {!s.can.configure && <p className="mf-sub">Changing a building's records setting is granted to named people only.</p>}
      {editing && (
        <form className="fr-form" onSubmit={save} aria-label="Records setting">
          {dropped.length > 0 && (
            <p className="mf-notice" role="status" data-testid="fr-dropped">
              No longer a current contact, so left out of this version: {dropped.join(', ')}.{!form.recipient_contact_record_id && form.channel === 'EMAIL' ? ' Choose the recipient.' : ''} Confirm the recipients again when you save.
            </p>
          )}
          <label>Channel
            <select value={form.channel} onChange={(e) => setForm({ ...form, channel: e.target.value })}>
              {s.vocabulary.channels.map((ch) => <option key={ch} value={ch}>{CHANNEL_LABEL[ch]}</option>)}
              {s.vocabulary.reserved_channels.map((ch) => <option key={ch} value={ch} disabled>{ch === 'ETHIZO' ? 'PCC through Ethizo (not available yet)' : ch}</option>)}
            </select>
          </label>
          <label>Recipient
            <select value={form.recipient_contact_record_id} onChange={(e) => setForm({ ...form, recipient_contact_record_id: e.target.value })}>
              <option value="">None</option>
              {s.contacts.map((ct) => <option key={ct.id} value={ct.id} disabled={!ct.email}>{ct.name || '—'} ({ct.role.replace(/_/g, ' ').toLowerCase()}) {ct.email || 'no email'}{ct.webmail ? ' · public webmail' : ''}</option>)}
            </select>
          </label>
          {form.channel === 'EMAIL' && (
            <fieldset className="fr-copies">
              <legend>Copies (up to {maxCopies}; {(form.cc_contact_record_ids || []).length} chosen)</legend>
              {s.contacts.filter((ct) => ct.id !== form.recipient_contact_record_id).map((ct) => {
                const chosen: string[] = form.cc_contact_record_ids || [];
                const on = chosen.includes(ct.id);
                const full = !on && chosen.length >= maxCopies;
                return (
                  <label key={ct.id}>
                    <input type="checkbox" checked={on} disabled={!ct.email || full}
                      onChange={(e) => setForm({ ...form, cc_contact_record_ids: e.target.checked ? [...chosen, ct.id] : chosen.filter((x) => x !== ct.id) })} />
                    {' '}{ct.name || '—'} ({ct.role.replace(/_/g, ' ').toLowerCase()}) {ct.email || 'no email'}{ct.webmail ? ' · public webmail' : ''}
                    {ct.notes && <span className={ct.do_not_send ? 'fr-note fr-note--stop' : 'fr-note'}> · Note: {ct.notes}</span>}
                  </label>
                );
              })}
              <p className="mf-sub">The recipients are confirmed together: adding, removing or changing any one needs them confirmed again.</p>
            </fieldset>
          )}
          <label>What is sent
            <select value={form.artifacts} onChange={(e) => setForm({ ...form, artifacts: e.target.value })}>
              <option value="AUDIOGRAM">Audiogram</option><option value="VISIT_SUMMARY">Visit summary</option><option value="BOTH">Both</option>
            </select>
          </label>
          {form.channel === 'EMAIL' && (
            <>
              <label><input type="checkbox" checked={Boolean(form.allow_webmail)} onChange={(e) => setForm({ ...form, allow_webmail: e.target.checked })} /> Allow a public webmail address (gmail.com and the like) for this building</label>
              {form.allow_webmail && (
                <label>Why public webmail is allowed here (required)
                  <Input value={form.webmail_reason} onChange={(e) => setForm({ ...form, webmail_reason: e.target.value })} maxLength={500} placeholder="e.g. the building has no mail of its own; the DON confirmed by phone" />
                </label>
              )}
            </>
          )}
          {form.channel === 'PCC_UPLOAD' && (
            <>
              <label>PCC file name
                <Input value={form.pcc_filename_pattern} onChange={(e) => setForm({ ...form, pcc_filename_pattern: e.target.value })} placeholder="{last}, {first} {dob} Audiology {visit_date}" />
              </label>
              <label>PCC document category
                <Input value={form.pcc_document_category} onChange={(e) => setForm({ ...form, pcc_document_category: e.target.value })} maxLength={120} />
              </label>
              <label><input type="checkbox" checked={Boolean(form.upload_is_confirmation)} onChange={(e) => setForm({ ...form, upload_is_confirmation: e.target.checked })} /> An upload into PCC counts as the facility's confirmation</label>
            </>
          )}
          <label>Reminder after (business days)
            <Input type="number" min={1} max={30} value={form.remind_after_business_days} onChange={(e) => setForm({ ...form, remind_after_business_days: e.target.value })} />
          </label>
          <label>Call the building after (business days)
            <Input type="number" min={1} max={60} value={form.escalate_after_business_days} onChange={(e) => setForm({ ...form, escalate_after_business_days: e.target.value })} />
          </label>
          <label>Note (optional)
            <Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} maxLength={500} />
          </label>
          <label><input type="checkbox" checked={form.confirmation_required} onChange={(e) => setForm({ ...form, confirmation_required: e.target.checked })} /> The facility confirms receipt</label>
          <label>Who at the facility asked, when and how (required)
            <Input value={form.request_basis} onChange={(e) => setForm({ ...form, request_basis: e.target.value })} maxLength={500} placeholder='e.g. "Jane Doe, DON, email of 10/20"' />
          </label>
          {form.channel === 'EMAIL' && (
            <div className="fr-on-file" data-testid="fr-on-file">
              <p className="mf-sub">Who will get the records:</p>
              <ul aria-label="Who will get the records">
                {chosenContacts.map(({ ct, primary }) => (
                  <li key={ct.id}>
                    {recipientLine({ primary, name: ct.name, role: ct.role, title: ct.title, email: ct.email })}
                    {ct.domain && <> · <span className="fr-domain">{ct.domain}</span></>}
                    {ct.new_domain && <> · <ToneChip tone="stop">new domain for this building</ToneChip></>}
                    {ct.do_not_send && <> · <ToneChip tone="stop">marked do not send</ToneChip></>}
                    {ct.notes && <> · <span className="fr-note">Note: {ct.notes}</span></>}
                    {ct.recent_change && <> · <ToneChip tone={ct.recent_change.by_you ? 'ok' : 'stop'}>{CHANGE_LABEL[ct.recent_change.change] || 'changed'} {day(ct.recent_change.at.slice(0, 10))} by {ct.recent_change.by_you ? 'you' : ct.recent_change.by_first_name}</ToneChip></>}
                  </li>
                ))}
              </ul>
              {markedDoNotSend.length > 0 && (
                <div className="fr-ack" data-testid="fr-do-not-send">
                  <p className="mf-sub">Marked &quot;do not send&quot; in Contacts:</p>
                  <ul aria-label="Marked do not send">
                    {markedDoNotSend.map(({ ct }) => <li key={ct.id}>{ct.name || ct.email || 'A contact'} ({ct.role.replace(/_/g, ' ').toLowerCase()}): &quot;{ct.notes}&quot;</li>)}
                  </ul>
                  <label><input type="checkbox" checked={Boolean(form.contacts_exclusion_ack)} onChange={(e) => setForm({ ...form, contacts_exclusion_ack: e.target.checked })} /> Send to them anyway: the note no longer applies</label>
                </div>
              )}
              <label><input type="checkbox" checked={Boolean(form.contacts_on_file)} onChange={(e) => setForm({ ...form, contacts_on_file: e.target.checked })} /> Send to these contacts on file</label>
              {form.contacts_on_file && needsAck.length > 0 && (
                <div className="fr-ack" data-testid="fr-ack">
                  <p className="mf-sub">Look again before sending to these contacts:</p>
                  <ul aria-label="Contacts to check">
                    {needsAck.map(({ ct }) => (
                      <li key={ct.id}>
                        {ct.name || ct.email || 'A contact'}
                        {ct.recent_change && !ct.recent_change.by_you ? ` was ${CHANGE_LABEL[ct.recent_change.change] || 'changed'} by ${ct.recent_change.by_first_name} on ${day(ct.recent_change.at.slice(0, 10))}` : ''}
                        {ct.new_domain ? `${ct.recent_change && !ct.recent_change.by_you ? ';' : ''} ${ct.domain} is a new domain for this building` : ''}
                      </li>
                    ))}
                  </ul>
                  <label><input type="checkbox" checked={Boolean(form.contacts_recent_change_ack)} onChange={(e) => setForm({ ...form, contacts_recent_change_ack: e.target.checked })} /> I checked these changed contacts</label>
                </div>
              )}
              {form.contacts_on_file && form.allow_webmail && (
                <p className="mf-notice mf-notice--stop" role="status" data-testid="fr-webmail-second">Public webmail is allowed in this version, so a second configure holder confirms the recipients: save without the tick, then another configure holder saves it with the tick (or verifies by call-back).</p>
              )}
            </div>
          )}
          <div className="fr-acts">
            <Button size="sm" type="submit">Save as a new version</Button>
            <Button size="sm" variant="ghost" type="button" onClick={() => setEditing(false)}>Cancel</Button>
          </div>
        </form>
      )}
    </div>
  );
}

/* ── The docked record ─────────────────────────────────────────────────────────────────────────── */

function BatchPanel({ row, onChanged }: { row: DeskRow; onChanged: () => void }) {
  const [batch, setBatch] = useState<Batch | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const load = useCallback(async () => {
    const r = await facilityRecordsApi.batch(row.facility_id, row.visit_date);
    if (r.success && r.data) { setBatch(r.data); setErr(null); } else setErr(refusalWords(r, 'The batch could not be loaded.'));
  }, [row.facility_id, row.visit_date]);
  useEffect(() => { load(); }, [load]);
  const act = useCallback(async (p: Promise<any>, done: string | ((r: any) => string)): Promise<string | null> => {
    try {
      const r = await p;
      if (!r.success) return refusalWords(r, 'Nothing was recorded.');
      setNotice(typeof done === 'function' ? done(r) : done);
      await load();
      onChanged();
      return null;
    } catch { return 'Could not reach eMerge. Nothing was changed.'; }
  }, [load, onChanged]);
  if (err) return <p className="mf-notice mf-notice--stop" role="alert">{err}</p>;
  if (!batch) return <MfSkeleton bare />;
  const inBatch = batch.residents.filter((r) => r.in_batch);
  const out = batch.residents.filter((r) => !r.in_batch);
  return (
    <div className="fr-panel">
      {batch.closed_out && <p data-testid="fr-closed-out"><ToneChip tone="ok">Visit closed out {when(batch.closed_out.at)} by {batch.closed_out.by_first_name}</ToneChip>{batch.closeouts.length > 1 ? ` (${batch.closeouts.length} close-outs)` : ''}</p>}
      <p><ViewChip view={batch.view} /> {batch.counts.in_batch} {batch.counts.in_batch === 1 ? 'resident' : 'residents'} · {batch.counts.files} {batch.counts.files === 1 ? 'file' : 'files'}{batch.completed ? ` · complete ${when(batch.completed.at)} by ${batch.completed.by_first_name}` : ''}</p>
      {notice && <p className="mf-notice" role="status">{notice}</p>}
      <Delivery batch={batch} act={act} />
      <h3 className="fr-h">Residents</h3>
      <ul className="fr-residents" aria-label="Residents in this visit's records">
        {inBatch.map((r) => <ResidentCard key={r.patient_id} batch={batch} r={r} act={act} />)}
        {out.map((r) => <ResidentCard key={r.patient_id} batch={batch} r={r} act={act} />)}
      </ul>
      {batch.can.prepare && batch.switches.writes && !batch.completed && <AddResident batch={batch} act={act} />}
      <details className="fr-history">
        <summary>History ({batch.history.length})</summary>
        <ul>{batch.history.map((h, i) => <li key={`${h.at}-${i}`}>{when(h.at)} · {h.event.toLowerCase().replace(/_/g, ' ')}{h.by_first_name ? ` · ${h.by_first_name}` : ''}{h.reason ? ` · ${h.reason}` : ''}</li>)}</ul>
      </details>
    </div>
  );
}

/* ── The desk ──────────────────────────────────────────────────────────────────────────────────── */

const SORTERS: Record<string, (r: DeskRow) => string | number | null> = {
  building: (r) => r.facility_name,
  visit: (r) => r.visit_date,
};

export default function FacilityRecordsDesk() {
  const [desk, setDesk] = useState<Desk | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [open, setOpen] = useOpenParam('open');
  const [legendKey, setLegendKey] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const load = useCallback(async () => {
    try {
      const r = await facilityRecordsApi.desk();
      if (r.success && r.data) { setDesk(r.data); setProblem(null); } else setProblem(refusalWords(r, 'Could not load facility records.'));
    } catch { setProblem('Could not reach eMerge.'); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const rows = useMemo(() => (desk ? desk.rows : []), [desk]);
  const shown = useMemo(() => rows
    .filter((r) => (legendKey ? r.view === legendKey : true))
    .filter((r) => (filter.trim() ? String(r.facility_name || '').toLowerCase().includes(filter.trim().toLowerCase()) : true)), [rows, legendKey, filter]);
  const sort = useClientSort<DeskRow>(shown, SORTERS);

  if (problem && !desk) return <main className="mf-main mf-main--board"><MfLoadFailure message={problem} retry={load} /></main>;
  if (!desk) return <main className="mf-main mf-main--board"><MfSkeleton /></main>;

  // Security review item 4: sending is on but the server has no Reply-To mailbox, so every send is refused.
  const replyToMissing = Boolean(desk.switches.send && desk.config && !desk.config.reply_to_configured);
  const legend: MfLegendItem[] = VIEWS.map((v) => ({ key: v, label: VIEW_LABEL[v], sample: <ViewChip view={v} />, meaning: VIEW_MEANS[v], count: desk.counts[v] || 0 }));
  const columns: BoardColumn<DeskRow>[] = [
    { key: 'visit', label: 'Visit day', sort: 'visit', help: 'The building visit day the records are from.', render: (r) => <span>{day(r.visit_date)}</span> },
    { key: 'state', label: 'State', help: 'Where the day\'s records stand (the legend above says each).', render: (r) => <ViewChip view={r.view} /> },
    { key: 'channel', label: 'Channel', help: 'How the building receives records, from its records setting.', render: (r) => (r.channel ? <span>{CHANNEL_LABEL[r.channel]}</span> : <span className="board-na">no setting</span>) },
    { key: 'residents', label: 'Residents', numeric: true, help: 'Residents in the batch (seen on rounds, with a manifest outcome, or added by a VA), and how many are ready.', render: (r) => <span>{r.counts.complete}/{r.counts.in_batch} ready{r.counts.held ? ` · ${r.counts.held} held` : ''}</span> },
    { key: 'files', label: 'Files', numeric: true, help: 'Files that would go: included audiograms (and visit summaries). For PCC, how many are uploaded.', render: (r) => <span>{r.counts.files}{r.channel === 'PCC_UPLOAD' ? ` · ${r.counts.uploaded} uploaded` : ''}</span> },
    { key: 'closeout', label: 'Closed out', help: 'When the provider closed out the visit (the check-out that sends), and who. A day not closed out is prepared by the VA.', render: (r) => (r.closed_out ? <span>{when(r.closed_out.at)} by {r.closed_out.by_first_name}{r.pending ? ` · ${r.pending} audiogram${r.pending === 1 ? '' : 's'} pending` : ''}</span> : <span className="board-na">not closed out</span>) },
    { key: 'delivery', label: 'Delivery', help: 'When it was sent or recorded, opens and downloads of the secure link, and the confirmation.', render: (r) => <span>{r.confirmed ? `Confirmed ${when(r.confirmed.at)}` : r.delivered_at ? `Sent ${when(r.delivered_at)} · opened ${r.opened} · ${r.downloads} downloads${r.reminder?.escalated ? ' · escalated' : r.reminder?.due ? ' · reminder due' : ''}` : '—'}</span> },
    { key: 'exceptions', label: 'Needs a person', help: 'The exceptions, in words. Nothing sends while one stands.', render: (r) => (r.exceptions.length ? <span className="board-clip" title={r.exceptions.map(exceptionWords).join('; ')}>{exceptionWords(r.exceptions[0])}{r.exceptions.length > 1 ? ` +${r.exceptions.length - 1}` : ''}</span> : <span className="board-na">none</span>) },
  ];
  const identity = {
    label: 'Building', help: 'The building visited. Open the row for its residents, files, delivery and records setting, docked beside the table.',
    render: (r: DeskRow) => <span className="board-clip" title={r.facility_name || ''}>{r.facility_name || 'Building'}</span>,
    name: (r: DeskRow) => `${r.facility_name || 'Building'} ${r.visit_date}`,
  };
  const sections = (r: DeskRow): BoardDetailSection[] => [
    { id: 'records', label: 'Visit records', render: () => <BatchPanel row={r} onChanged={load} /> },
    { id: 'setting', label: 'Records setting', render: () => <SettingPanel facilityId={r.facility_id} onChanged={load} />, defaultCollapsed: true },
  ];
  const state = rows.length === 0
    ? <TideState kind="empty" message="No building visit days with residents seen in the last two months." />
    : shown.length === 0 ? <TideState kind="empty" reason="filtered" message="No building visit day matches." /> : undefined;

  return (
    <main className="mf-main mf-main--board" data-testid="facility-records-desk">
      <MfBoard<DeskRow>
        title="Facility records"
        count={rows.length}
        units={['building visit day', 'building visit days']}
        what={<>Each building visit day's records for the facility: the audiograms filed on the residents seen, sent by secure link, uploaded into PCC, or recorded as sent outside eMerge, then confirmed. {desk.switches.send ? '' : 'Sending from eMerge is switched off on this server.'}{desk.switches.writes ? '' : ' Changes are switched off on this server.'}</>}
        controls={<Input className="mf-filter" value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filter by building" aria-label="Filter by building" />}
        legend={<MfLegend title="Records state" items={legend} whole={rows.length} units={['building visit day', 'building visit days']} exclusive value={legendKey} onSelect={setLegendKey}
          scope="Each entry is where a building visit day's records stand, with how many days are there. Press one to show only those." />}
        notice={problem || replyToMissing ? (
          <>
            {problem && <p className="mf-notice mf-notice--stop" role="alert">{problem}</p>}
            {replyToMissing && <p className="mf-notice mf-notice--stop" role="status" data-testid="fr-reply-to-missing">Reply-To not configured: this server has no reply mailbox for facility records (FACILITY_RECORDS_REPLY_TO), so nothing is sent from eMerge until it is set.</p>}
          </>
        ) : null}
        state={state}
        columns={columns}
        identity={identity}
        rows={sort.rows}
        rowKey={(r) => r.key}
        openKey={open}
        onOpenChange={setOpen}
        detailSections={sections}
        detailLabel={(r) => `${r.facility_name || 'Building'}: records of ${day(r.visit_date)}`}
        sort={sort.sort}
        dir={sort.dir}
        sorts={sort.sorts}
        onSort={sort.onSort}
        notes={desk.capped ? ['Only the most recent 600 building visit days are listed.'] : undefined}
      />
    </main>
  );
}
